import { useRef, useState } from 'react';

export interface RecordEditorRules<TValues, TRecord, TErrors extends object> {
  toForm: (record: TRecord) => TValues;
  validate: (values: TValues) => TErrors;
  isDirty: (base: TValues, values: TValues) => boolean;
  /** What a refused save means for the form. */
  saveErrorToForm: (error: unknown) => TErrors;
}

export interface RecordEditorOptions<TValues, TRecord> {
  initial: TValues;
  /** Show "Saved" from the start, as after a create lands on the record's page. */
  initiallySaved?: boolean;
  persist: (values: TValues, base: TValues) => Promise<TRecord>;
  onSaved?: (record: TRecord) => void;
}

export interface RecordEditor<TValues, TErrors> {
  values: TValues;
  /** The values as last saved (or as loaded). */
  base: TValues;
  set: (patch: Partial<TValues>) => void;
  errors: TErrors;
  dirty: boolean;
  saved: boolean;
  saving: boolean;
  submit: () => Promise<void>;
}

const hasErrors = (errors: object) => Object.keys(errors).length > 0;

export function useRecordEditor<TValues, TRecord, TErrors extends object>(
  rules: RecordEditorRules<TValues, TRecord, TErrors>,
  options: RecordEditorOptions<TValues, TRecord>,
): RecordEditor<TValues, TErrors> {
  const [base, setBase] = useState(options.initial);
  const [values, setValues] = useState(options.initial);
  const [tried, setTried] = useState(false);
  const [saved, setSaved] = useState(options.initiallySaved ?? false);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<Partial<TErrors>>({});
  // State would only show the save as started after a re-render.
  const inFlight = useRef(false);

  const dirty = rules.isDirty(base, values);
  const ownErrors = rules.validate(values);
  const errors = { ...(tried ? ownErrors : {}), ...serverErrors } as TErrors;

  const set = (patch: Partial<TValues>) => {
    setValues((current) => ({ ...current, ...patch }));
    setSaved(false);
    setServerErrors({});
  };

  const submit = async () => {
    setTried(true);
    if (hasErrors(ownErrors) || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setServerErrors({});
    const sent = values;
    try {
      const record = await options.persist(sent, base);
      const fresh = rules.toForm(record);
      setBase(fresh);
      // Anything typed while the save was in flight stays, and reads as unsaved.
      setValues((current) => (current === sent ? fresh : current));
      setTried(false);
      setSaved(true);
      options.onSaved?.(record);
    } catch (error) {
      setServerErrors(rules.saveErrorToForm(error));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return { values, base, set, errors, dirty, saved: saved && !dirty, saving, submit };
}
